import React from 'react';
import { Select, Flex, Text, LoadingOrbit, type SelectOption } from '@chatsdet/ui';
import { useOrgCredentials, type OrgCredential } from '../../hooks/useOrgCredentials';
import { usePropertyCredentialSummaries } from '../../hooks/usePropertyMetadata';

type BaseCredentialSelectProps = {
  selectedCredentialId: string | null;
  onSelect: (credentialId: string | null) => void;
  isDisabled?: boolean;
  placeholder?: string;
  label?: string;
  className?: string;
  /** Shows the required-field asterisk on the label; purely visual, does not affect gating. */
  isRequired?: boolean;
  /** When set, show credentials scoped to this environment plus unscoped credentials. */
  filterEnvironmentId?: string | null;
  /** Show "All credentials" option at the top (for filter selects) */
  showAllOption?: boolean;
  /**
   * Offer two extra options above the credentials: an explicit "No credential
   * (anonymous)" entry and "Provision a temporary test identity" (UX sign-up
   * walkthroughs). Mutually exclusive with picking a real credential.
   */
  showProvisionOption?: boolean;
  /** Whether the provision option is the current selection (credentialId stays null). */
  isProvisionSelected?: boolean;
  /** Called when the user picks the provision option. */
  onSelectProvision?: () => void;
  /**
   * Offer an explicit "Signed out (no credential)" entry, distinct from the
   * unset placeholder. Use this when the caller needs to tell "the user
   * hasn't chosen yet" (`selectedCredentialId === null`, nothing selected,
   * placeholder shown) apart from "the user deliberately chose to review
   * signed out" (`selectedCredentialId === null` AND `isSignedOutSelected`).
   * Independent of `showProvisionOption`.
   */
  showSignedOutOption?: boolean;
  /** True when `selectedCredentialId === null` represents a deliberate signed-out choice, not an unset field. */
  isSignedOutSelected?: boolean;
  /** Called when the user picks the signed-out option. */
  onSelectSignedOut?: () => void;
};

type OrgScopedProps = BaseCredentialSelectProps & {
  /** Omit propertyId to fetch all org credentials */
  propertyId?: undefined;
};

type PropertyScopedProps = BaseCredentialSelectProps & {
  /** Pass propertyId to scope credentials to a single property */
  propertyId: string;
};

type CredentialSelectProps = OrgScopedProps | PropertyScopedProps;

const ALL_CREDENTIALS_ID = '__all__';
const ANONYMOUS_ID = '__anonymous__';
const PROVISION_ID = '__provision_temp_identity__';
const SIGNED_OUT_ID = '__signed_out__';

function toRichOptions(credentials: OrgCredential[], showAllOption: boolean): SelectOption[] {
  const credentialOptions: SelectOption[] = credentials.map((c) => ({
    id: c.id,
    label: c.name,
    description: c.propertyName ?? undefined,
    tags:
      c.lastLoginSucceededAt || c.storageStateS3Key
        ? [{ label: 'Verified', variant: 'success' as const }]
        : [],
  }));
  if (showAllOption) {
    return [{ id: ALL_CREDENTIALS_ID, label: 'All credentials' }, ...credentialOptions];
  }
  return credentialOptions;
}

export function CredentialSelect({
  propertyId,
  selectedCredentialId,
  onSelect,
  isDisabled,
  placeholder,
  label,
  className,
  isRequired,
  filterEnvironmentId,
  showAllOption = false,
  showProvisionOption = false,
  isProvisionSelected = false,
  onSelectProvision,
  showSignedOutOption = false,
  isSignedOutSelected = false,
  onSelectSignedOut,
}: CredentialSelectProps) {
  // Use org-scoped or property-scoped query based on props
  const orgQuery = useOrgCredentials({ enabled: !propertyId });
  const propertyQuery = usePropertyCredentialSummaries(propertyId ?? '');

  const isLoading = propertyId ? propertyQuery.isLoading : orgQuery.isLoading;
  const isError = propertyId ? propertyQuery.isError : orgQuery.isError;

  const credentials: OrgCredential[] = React.useMemo(() => {
    if (propertyId) {
      // Property-scoped credentials don't have full OrgCredential shape,
      // so we map them to a compatible shape
      const items = propertyQuery.data?.items ?? [];
      return items.map((c) => ({
        id: c.id,
        name: c.name,
        environmentId: c.environmentId ?? null,
        propertyId,
        propertyName: null,
        lastLoginSucceededAt: c.lastLoginSucceededAt ?? null,
        storageStateS3Key: c.storageStateS3Key ?? null,
      }));
    }
    return orgQuery.credentials;
  }, [propertyId, propertyQuery.data, orgQuery.credentials]);

  const visibleCredentials = React.useMemo(() => {
    if (!filterEnvironmentId) return credentials;
    return credentials.filter(
      (credential) =>
        credential.environmentId === filterEnvironmentId || credential.environmentId === null
    );
  }, [credentials, filterEnvironmentId]);

  const options = React.useMemo(() => {
    let result = toRichOptions(visibleCredentials, showAllOption);
    if (showProvisionOption) {
      result = [
        { id: ANONYMOUS_ID, label: 'No credential (anonymous)' },
        {
          id: PROVISION_ID,
          label: 'Provision a temporary test identity',
          description:
            'Random email (with a live inbox) + password — the agent signs up itself. Starts logged out.',
        },
        ...result,
      ];
    }
    if (showSignedOutOption) {
      result = [{ id: SIGNED_OUT_ID, label: 'Signed out (no credential)' }, ...result];
    }
    return result;
  }, [visibleCredentials, showAllOption, showProvisionOption, showSignedOutOption]);

  const defaultPlaceholder = showAllOption ? 'All credentials' : 'Select a credential';

  const selectedKey = React.useMemo(() => {
    if (showProvisionOption && isProvisionSelected) return PROVISION_ID;
    if (showSignedOutOption && isSignedOutSelected) return SIGNED_OUT_ID;
    if (showAllOption && !selectedCredentialId) return ALL_CREDENTIALS_ID;
    if (!selectedCredentialId) return showProvisionOption ? ANONYMOUS_ID : null;
    const exists = visibleCredentials.some((c) => c.id === selectedCredentialId);
    if (exists) return selectedCredentialId;
    if (showAllOption) return ALL_CREDENTIALS_ID;
    return showProvisionOption ? ANONYMOUS_ID : null;
  }, [
    selectedCredentialId,
    visibleCredentials,
    showAllOption,
    showProvisionOption,
    isProvisionSelected,
    showSignedOutOption,
    isSignedOutSelected,
  ]);

  React.useEffect(() => {
    if (
      selectedCredentialId &&
      !visibleCredentials.some((credential) => credential.id === selectedCredentialId)
    ) {
      onSelect(null);
    }
  }, [selectedCredentialId, visibleCredentials, onSelect]);

  const handleSelectionChange = (key: React.Key | null) => {
    if (key === PROVISION_ID) {
      onSelectProvision?.();
    } else if (key === SIGNED_OUT_ID) {
      if (onSelectSignedOut) onSelectSignedOut();
      else onSelect(null);
    } else if (key === ALL_CREDENTIALS_ID || key === ANONYMOUS_ID || key === null) {
      onSelect(null);
    } else {
      onSelect(String(key));
    }
  };

  if (isLoading) {
    return (
      <Flex align="center" gap="sm" className={className}>
        <LoadingOrbit size="sm" />
        <Text size="sm" className="text-muted-foreground">
          Loading credentials...
        </Text>
      </Flex>
    );
  }

  if (options.length === 0 && !showAllOption && !showProvisionOption && !showSignedOutOption) {
    return (
      <Text size="sm" className="text-muted-foreground">
        No credentials available yet. Add credentials to your applications first.
      </Text>
    );
  }

  return (
    <Select
      key={
        selectedCredentialId ??
        (isProvisionSelected ? PROVISION_ID : isSignedOutSelected ? SIGNED_OUT_ID : 'all')
      }
      label={label}
      placeholder={placeholder ?? defaultPlaceholder}
      items={options}
      selectedKey={selectedKey}
      onSelectionChange={handleSelectionChange}
      isDisabled={isDisabled || isError}
      isRequired={isRequired}
      className={className}
    />
  );
}
